/**
 * 稳定引用 Hook
 *
 * 将任意值（尤其是回调函数）存入 ref，每次渲染时同步更新。
 * 用于在 useCallback / useEffect 中安全读取最新值，同时避免将不稳定的
 * 内联函数/对象放入依赖数组导致无限更新。
 *
 * 采用 render-time 直接赋值模式（而非 useEffect），确保 ref 在 render 阶段
 * 即刻同步，避免 render → commit 之间的 stale 窗口。
 */

import { useRef } from 'react';

/**
 * 返回一个始终指向最新 `value` 的 ref 对象
 *
 * @example
 * ```ts
 * const onSuccessRef = useLatestRef(options.onSuccess);
 *
 * const submit = useCallback(async () => {
 *   const result = await api();
 *   onSuccessRef.current?.(result); // 始终调用最新回调
 * }, [api]); // 无需将 onSuccess 放入依赖
 * ```
 */
export function useLatestRef<T>(value: T): React.MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
